import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ScreenType } from 'src/app/enums/enums';
import { ErrorMsg } from 'src/app/models/error/error-msg.model';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { FormService } from 'src/app/services/form.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { DeliveryPointFormData } from 'src/app/models/delivery-courier/delivery-point-form-data.model';
import { SubmitButtonComponent } from '../../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { FormInputControlComponent } from '../../../common/form-input-control/form-input-control.component';
import { FormHeaderComponent } from '../../../common/form-header/form-header.component';
import { ApiService } from 'src/app/services/api.service';
import { firstValueFrom, Observable, take } from 'rxjs';
import {
  emailValidator,
  mobilePhoneValidator,
} from 'src/app/common/validatos/validators';
import { CaptchaDialogService } from 'src/app/services/captcha-dialog.service';
import { FormErrorMessageComponent } from '../../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';
import { FormPointInputFieldComponent } from '../../../common/form-point-input-field/form-point-input-field.component';
import { PointDto } from 'src/app/models/fds2-points/fds2-points.model';
import { Store } from '@ngrx/store';
import { selectSelectedPartners } from 'src/app/store/forms-data/forms-data.selectors';

@Component({
  selector: 'app-delivery-point-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    FormInputControlComponent,
    FormHeaderComponent,
    FormErrorMessageComponent,
    FormPointInputFieldComponent,
  ],
  templateUrl: './delivery-point-form.component.html',
  styleUrl: './delivery-point-form.component.scss',
})
export class DeliveryPointFormComponent implements AfterViewInit {
  ScreenType = ScreenType;

  pointForm!: FormGroup;
  submitException = signal(false);
  isSubmitLoading = signal(false);
  selectedPoint: PointDto | undefined;
  selectedPartners$: Observable<string[]>;

  partners = [
    { name: 'ABC', image: 'assets/images/abc.png' },
    { name: 'Delikatesy', image: 'assets/images/delikatesy.png' },
    { name: 'Duży Ben', image: 'assets/images/duzyben.png' },
    { name: 'Groszek', image: 'assets/images/groszek.png' },
    { name: 'Shell', image: 'assets/images/shell1.png' },
    { name: 'Stokrotka', image: 'assets/images/stokrotka.png' },
    { name: 'Żabka', image: 'assets/images/zabka.png' },
    { name: 'Epaka', image: 'assets/images/epaka.png' },
    { name: 'Furgonetka', image: 'assets/images/furgonetka.png' },
    { name: 'Kolporter', image: 'assets/images/kolporter3.png' },
  ];

  constructor(
    private apiService: ApiService,
    private navigationService: NavigationService,
    private fb: FormBuilder,
    public formService: FormService,
    private cdr: ChangeDetectorRef,
    private captchaDialogService: CaptchaDialogService,
    protected storeService: StoreService,
    private store: Store
  ) {
    this.initializeForm();
    this.selectedPartners$ = this.store.select(selectSelectedPartners);
  }

  private initializeForm() {
    const formData = this.storeService.getDeliveryPointFormData();

    this.pointForm = this.fb.group({
      name: [formData?.name, [Validators.required]],
      company: [formData?.company],
      zip_code: [
        formData?.zipCode,
        [Validators.required, Validators.pattern('^[0-9]{2}-[0-9]{3}$')],
      ],
      city: [formData?.city, [Validators.required]],
      street: [formData?.street, [Validators.required]],
      house_number: [formData?.houseNumber, [Validators.required]],
      apartment_number: [formData?.apartmentNumber],
      phone: [formData?.phone, [Validators.required, mobilePhoneValidator()]],
      email: [formData?.email, [Validators.required, emailValidator()]],
      pointName: [formData?.pointName, [Validators.required]],
      pointId: [
        { value: formData?.pointId, disabled: true },
        [Validators.required],
      ],
      pointZipCode: [{ value: formData?.pointZipCode, disabled: true }],
      pointPartner: [{ value: formData?.pointPartner, disabled: true }],
      pointPartnerCode: [{ value: formData?.pointPartnerCode, disabled: true }],
      pointCity: [{ value: formData?.pointCity, disabled: true }],
      pointAddress: [{ value: formData?.pointAddress, disabled: true }],
    });

    this.formService.markAllNotEmptyAsTouched(this.pointForm);
    this.pointForm.valueChanges.subscribe(() => this.saveFormDataToCache());
    this.zipCode?.valueChanges.subscribe(() => {
      this.city?.updateValueAndValidity({ emitEvent: false });
    });

    this.city?.valueChanges.subscribe(() => {
      this.zipCode?.updateValueAndValidity({ emitEvent: false });
    });
  }

  get name() {
    return this.pointForm.get('name');
  }
  get company() {
    return this.pointForm.get('company');
  }
  get zipCode() {
    return this.pointForm.get('zip_code');
  }
  get city() {
    return this.pointForm.get('city');
  }
  get street() {
    return this.pointForm.get('street');
  }
  get houseNumber() {
    return this.pointForm.get('house_number');
  }
  get apartmentNumber() {
    return this.pointForm.get('apartment_number');
  }
  get phone() {
    return this.pointForm.get('phone');
  }
  get email() {
    return this.pointForm.get('email');
  }

  get pointName() {
    return this.pointForm.get('pointName');
  }

  get pointId() {
    return this.pointForm.get('pointId');
  }

  get pointZipCode() {
    return this.pointForm.get('pointZipCode');
  }

  get pointPartner() {
    return this.pointForm.get('pointPartner');
  }

  get pointPartnerCode() {
    return this.pointForm.get('pointPartnerCode');
  }

  get pointCity() {
    return this.pointForm.get('pointCity');
  }

  get pointAddress() {
    return this.pointForm.get('pointAddress');
  }

  ngAfterViewInit() {
    this.handleFormErrors();
  }

  private handleFormErrors() {
    const errorResponse = this.storeService.getErrorResponse();
    let errorMsgCopy: ErrorMsg[] | undefined = undefined;

    if (errorResponse) {
      errorMsgCopy = errorResponse?.errorMsg?.filter((err) => {
        let control: any;
        if (err.idRow === 2 || err.idRow === 3) {
          switch (err.field) {
            case 'name':
              control = this.name;
              break;
            case 'companyName':
              control = this.company;
              break;
            case 'city':
              control = this.city;
              break;
            case 'postalCode':
              control = this.zipCode;
              break;
            case 'address1':
              control = this.street;
              break;
            case 'phone':
              control = this.phone;
              break;
            case 'email':
              control = this.email;
              break;
            case 'ext2Id':
              control = this.pointName;
              break;
            default:
              control = null;
          }

          if (control) {
            control.setErrors({ [err.errorCode]: true });
            control.markAsTouched();
            return false; // Usuwa err z listy errorMsg
          }
        }

        return true; // Zatrzymuje err w liście errorMsg
      });
    }

    const errorRespCpy: ErrorResponse = {
      httpStatus: errorResponse?.httpStatus,
      errorMsg: errorMsgCopy,
    };

    this.storeService.setPickupErrorResponse(errorRespCpy);
    this.cdr.detectChanges();
  }

  saveFormDataToCache(): void {
    const deliveryPointFormData: DeliveryPointFormData = {
      name: this.name?.value,
      company: this.company?.value,
      zipCode: this.zipCode?.value,
      city: this.city?.value,
      street: this.street?.value,
      houseNumber: this.houseNumber?.value,
      apartmentNumber: this.apartmentNumber?.value,
      phone: this.phone?.value,
      email: this.email?.value,
      pointId: this.pointId?.value,
      pointName: this.pointName?.value,
      pointZipCode: this.pointZipCode?.value,
      pointPartner: this.pointPartner?.value,
      pointPartnerCode: this.pointPartnerCode?.value,
      pointCity: this.pointCity?.value,
      pointAddress: this.pointAddress?.value,
    };

    this.storeService.setDeliveryCourierFormData(undefined);
    this.storeService.setDeliveryPointFormData(deliveryPointFormData);
  }

  backButtonAction() {
    this.storeService.setSelectedPartners([]);
    this.navigationService.changeScreenTo(ScreenType.UTWORZ_PRZESYLKE);
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.pointForm.invalid) {
      this.formService.markAllAsTouched(this.pointForm);
      this.isSubmitLoading.set(false);
      return;
    }

    this.saveFormDataToCache();

    try {
      const cityZipCodeResponse = await firstValueFrom(
        this.apiService.submitCityZipCodeRequest({
          zipCode: this.zipCode?.value,
          city: this.city?.value,
        })
      );
      this.submitException.set(false);
      this.storeService.setSelectedPartners([]);
      this.navigationService.changeScreenTo(ScreenType.OPCJE_NADANIA);
    } catch (error) {
      this.handleCityZipCodeError(error);
      return;
    } finally {
      this.isSubmitLoading.set(false);
    }
  }

  handleCityZipCodeError(error: any) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };

      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else if (errorResponse?.errorMsg?.[0]?.errorCode === 'INCONSISTENT') {
        this.zipCode?.setErrors({ zip_code_city_inconsistent: true });
        this.city?.setErrors({ zip_code_city_inconsistent: true });
      } else {
        this.submitException.set(true);
      }
    } catch (error) {
      this.submitException.set(true);
    }
  }

  onInputChanged(
    event: Event,
    childComponent: FormPointInputFieldComponent
  ): void {
    const inputValue = (event.target as HTMLInputElement).value;
    childComponent.onInputChange(inputValue);
  }

  onInputFocus(
    focusValue: boolean,
    childComponent: FormPointInputFieldComponent
  ) {
    if (focusValue === false) {
      childComponent.onInputChange('');
    }
    childComponent.onInputFocus(focusValue);
  }

  onSelectedPoint(point: PointDto): void {
    this.pointName?.setValue(point.pointName);
    this.pointId?.setValue(point.pointCode);
    this.pointZipCode?.setValue(point.postalCode);
    this.pointPartner?.setValue(point.partnerName);
    this.pointPartnerCode?.setValue(point.partnerCode);
    this.pointCity?.setValue(point.city);
    this.pointAddress?.setValue(point.address1);
  }

  isTooltipVisible = false;

  showTooltip(): void {
    this.isTooltipVisible = true;
  }

  hideTooltip(): void {
    this.isTooltipVisible = false;
  }

  toggleSelection(partnerName: string): void {
    this.selectedPartners$.pipe(take(1)).subscribe((selectedPartners) => {
      const current = selectedPartners ?? [];
      const isAlreadySelected = current.includes(partnerName);

      const updatedSelected = isAlreadySelected
        ? current.filter((name) => name !== partnerName)
        : [...current, partnerName];

      this.storeService.setSelectedPartners(updatedSelected);
    });
  }
}
